/********************* Klaviyo Popup Suppression *********************/

// the email capture popup is the path for visitors arriving without a code, so suppress it for
// anyone who already has one. add only: once they hold a discount the popup has nothing to offer
function hideEmailCapturePopup() {
    document.documentElement.classList.add("hasDiscountCode");
}

// the cookie case runs here, ahead of klaviyo, so the popup never flashes in first. a code applied
// in the cart is stamped later, from syncWelcomeDiscountToCart
if (Cookies.get("discount_code")) {
    hideEmailCapturePopup();
}

// shopify's /discount/CODE route sets this cookie so the code survives all the way to checkout
function clearDiscountCodeCookie() {
    Cookies.remove("discount_code", {path: "/"});
}

/********************* Welcome Discount Code *********************/

let welcomeDiscount = {}

// single source of truth for whether we have a code worth advertising. the pdps, the popup and the
// mini cart all rely on this
let hasWelcomeCode = false;

// look up a discount code. returns null for anything we cannot quote a figure for
async function lookupDiscountCode(code) {
    let response;

    try {
        response = await fetch(`${root}/discount/get-code.php?code=${encodeURIComponent(code)}`).then(response => response.json());
    } catch (error) {
        console.error(`Could not look up discount ${code}.`);
        return null;
    }

    // the api refuses any code it cannot hand us a percentage and a scope for
    if (response.error) {
        return null;
    }

    return {percent: response.percent, scope: response.scope};
}

// get the discount code from the discount_code cookie
async function getDiscountCode() {
    const cookieCode = Cookies.get("discount_code");

    // nothing in the cookie means there is no code to look up
    if (!cookieCode) {
        return;
    }

    const cookieEntry = await lookupDiscountCode(cookieCode);

    // a code the store cannot honour advertises nothing
    if (!cookieEntry) {
        return;
    }

    // keep the cookie's casing for anything we display
    welcomeDiscount = {
        code: cookieCode,
        percent: cookieEntry.percent,
        scope: cookieEntry.scope
    }

    // single source of truth for whether we have a code worth advertising. the pdps, the popup and the
    // mini cart all gate their discount language on this, so a cookie we do not recognise shows nothing
    hasWelcomeCode = !!welcomeDiscount.code;
}

// whether the code discounts the purchase option currently selected. a subscription scoped code only
// pays out on a subscription; an all scoped one pays out either way
function welcomeDiscountApplies(isSub) {
    if (!hasWelcomeCode) {
        return false;
    }

    return isSub || welcomeDiscount.scope === "all";
}

// check if we have a discount code currently applied
function welcomeDiscountApplied() {
    if (!hasWelcomeCode) {
        return false;
    }
    return !!cart.discount_codes?.some(code => {
        return code.code.toUpperCase() === welcomeDiscount.code.toUpperCase() && code.applicable === true;
    });
}

// any code shopify confirms applies to this cart, to ensure cart does not render blank while
// code is applied
function cartDiscountApplied() {
    return !!cart.discount_codes?.some(code => code.applicable === true);
}

// shopify refuses a valid code on an empty cart because there are no line items to allocate against,
// so a verdict only means anything once something is in the cart
function welcomeCodeRefused() {
    if (!hasWelcomeCode || cart.item_count === 0) {
        return false;
    }
    return !!cart.discount_codes?.some(code => {
        return code.code.toUpperCase() === welcomeDiscount.code.toUpperCase() && code.applicable === false;
    });
}

/********************* Discount Source Tracking *********************/

// sessionStorage key holding the code our own welcome popup last sent someone away with. written by
// theme-welcome-popup.js on the way out, read here. it has to outlive the navigation and then some:
// a code only lands on the cart once there are items in it, which can be several pages later
const WELCOME_SOURCE_KEY = "welcomeDiscountSource";

// the code the current source stamp was written for. hidden attribute, so it stays out of the order's
// customer facing attributes while living exactly as long as the cart does
function stampedCode() {
    return cart.attributes?._discount_source_code || "";
}

// where the applied code came from. a popup link and an ad link both arrive through shopify's
// /discount/CODE route and are indistinguishable on the cart, so the popup marks its own
// navigation and that mark is the only thing that separates the two arms
function discountSourceMethod(code) {
    let method = "embedded_link";

    if (sessionStorage.getItem(WELCOME_SOURCE_KEY)?.toUpperCase() === code.toUpperCase()) {
        method = "welcome_popup";
    }

    return method;
}

// stamp how the applied code got here, unless something already did. shopify puts a link borne code
// on the cart before any of our js runs, so this is what attributes those. manual entry stamps itself
async function recordDiscountSource() {
    const appliedCode = cart.discount_codes?.find(code => code.applicable === true);

    // no code applied means there is nothing to attribute
    if (!appliedCode) {
        return;
    }

    // already stamped for this exact code, so leave it alone. anything else means the applied code has
    // changed since the last stamp and the stamp is now describing a code that is gone
    if (appliedCode.code.toUpperCase() === stampedCode().toUpperCase()) {
        return;
    }

    cart = await updateCart({
        attributes: {
            _discount_source_method: discountSourceMethod(appliedCode.code),
            _discount_source_code: appliedCode.code
        }
    });
}

// resolves once the cookie code has been looked up and reconciled against whatever the cart already
// carries. the pdps, the popup and the mini cart all await this before rendering a discount figure
const welcomeCodeReady = (async () => {
    // the liquid bootstrapped cart omits discount_codes and everything below needs them. item_count is
    // accurate on it though, so a visitor landing with an empty cart still costs no round trip
    let cartFetch = null;
    if (cart.item_count > 0) {
        cartFetch = fetch("/cart.js").then(response => response.json());
    }

    // neither needs the other, and every pdp and openCart waits on this promise
    const [fetchedCart] = await Promise.all([cartFetch, getDiscountCode()]);

    if (fetchedCart) {
        cart = fetchedCart;
    }

    // the cart outranks the cookie. a code typed into the drawer replaces the link's code, so the pdp
    // has to quote what is applied now rather than what the cookie still remembers from the link
    await syncWelcomeDiscountToCart();
})();

// the applied code drives every rendered figure. a code we cannot quote a figure for still sits on
// the cart and still discounts at checkout, it just stops driving our rendering - shopify displays
// it natively in the subtotal and at checkout
async function syncWelcomeDiscountToCart() {
    const appliedCode = cart.discount_codes?.find(code => code.applicable === true);

    if (!appliedCode) {
        return;
    }

    // shopify is honouring a code, so they hold a discount however it got here - typed into the
    // drawer, carried in on a link - and the email capture popup should stay down
    hideEmailCapturePopup();

    // what we are quoting right now, so the page is only asked to re-render when it actually moves
    const previousCode = welcomeDiscount.code || "";
    const previousPercent = welcomeDiscount.percent || 0;

    // look up before the reset below, so on screen figures hold their value across the round trip
    const appliedEntry = await lookupDiscountCode(appliedCode.code);

    // unquotable by default: every enhanced figure disappears and shopify renders the code on its own
    welcomeDiscount = {};
    hasWelcomeCode = false;

    // quotable: re-render everything to this code, whether it arrived in a link or was typed in
    if (appliedEntry) {
        welcomeDiscount = {
            code: appliedCode.code,
            percent: appliedEntry.percent,
            scope: appliedEntry.scope
        }

        hasWelcomeCode = true;
    }

    // the pdps render off welcomeDiscount but have no way to notice it moved under them, and the mini
    // cart can swap the applied code at any time. an event rather than a direct call keeps this file
    // out of knowing which product template is on screen, since each one names its renderer differently
    if ((welcomeDiscount.code || "") === previousCode && (welcomeDiscount.percent || 0) === previousPercent) {
        return;
    }

    document.dispatchEvent(new CustomEvent("welcomeDiscountChange"));
}

/********************* Welcome Discount Popup *********************/

// sessionStorage key so the popup only toggles once, not on every pdp they browse
const WELCOME_POPUP_KEY = "welcomeDiscountShown";

// confirms the auto-applied discount once per session.
// popup comes from snippets/welcome-congrats.liquid, so every pdp shares one implementation
function initWelcomePopup() {
    const popup = document.querySelector('#welcomeCongrats');

    // nothing to do on pages that do not render the snippet, if the discount cookie is empty, or once we
    // have already announced it this session
    if (!popup || !Cookies.get("discount_code") || sessionStorage.getItem(WELCOME_POPUP_KEY)) {
        return;
    }

    sessionStorage.setItem(WELCOME_POPUP_KEY, "1");

    // holds the auto close timer so a manual close can cancel it
    let congratsTimer = null;

    // close popup function
    function closeCongrats() {
        // drop the auto close timer, otherwise it fires again after a manual close
        clearTimeout(congratsTimer);

        popup.classList.remove('active');

        // wait for the transition to finish before actually closing
        setTimeout(() => {
            popup.close();
            document.querySelector('body').classList.remove('noScroll');
        }, 650);
    }

    // x icon click close
    popup.querySelector('#welcomeCongratsClose').addEventListener('click', closeCongrats);

    // outside click close
    popup.addEventListener('click', function (e) {
        if (e.target === this) {
            closeCongrats();
        }
    });

    // accessibility close
    popup.addEventListener('close', closeCongrats);

    // hold until the code has been looked up
    (async () => {
        await welcomeCodeReady;

        // check that we have a discount code to announce for
        if (!hasWelcomeCode) {
            return;
        }

        // fill the copy before showing so it never paints empty
        popup.querySelector('.welcomeCongratsCheck.success').classList.add('active');
        popup.querySelector('#welcomeCongratsInner').insertAdjacentHTML("beforeend", `<p id="welcomeCongratsText">Congrats! Your <strong>${welcomeDiscount.percent}% off ${welcomeDiscount.code} discount</strong> has been auto-applied.</p>`);

        popup.showModal();
        popup.classList.add('active');

        // auto close popup after 6 seconds
        congratsTimer = setTimeout(closeCongrats, 6000);
    })();
}

initWelcomePopup();
